import { propertyEventTracker } from '@propertypal/shared/src/reducers/properties';
import axios from '@propertypal/shared/src/services/axios';
import { Agent, AgentEventPage } from '@propertypal/shared/src/types/agents';
import { Contact, Property } from '@propertypal/shared/src/types/property';
import CallAgentModal, { AgentNumber } from '@propertypal/web-ui/src/modal/CallAgentModal';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import config from '../../../../app.config.json';

const REVEAL_TEXT = 'Reveal Phone Number';

const useCallAgent = () => {
  const [modal, setModal] = useState<React.ReactElement | undefined>();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [revealText, setRevealText] = useState<string>(REVEAL_TEXT);
  const dispatch = useDispatch();

  const trackCall = (property: Property, accountNumberSelected: string) => {
    dispatch(
      propertyEventTracker(property.id, property.type === 'development', {
        eventType: 'PHONE_REVEAL',
        accountNumber: accountNumberSelected,
      }),
    );
  };

  const trackAgentCall = async (agent: Agent, page: AgentEventPage) => {
    await axios(
      {
        method: 'POST',
        url: `${config.apiUrl}/account/${agent.accountNumber}/event`,
        data: {
          eventType: 'PHONE_REVEAL',
          eventPageType: page,
        },
      },
      dispatch,
    );
  };

  const getPhoneNumber = (property: Property, contact?: Contact) => {
    if (contact) {
      trackCall(property, contact.accountNumber);
      setPhoneNumber(contact.phoneNumber.displayNumber);
    }
  };

  const callAgent = async (property?: Property, contact?: Contact, agent?: Agent) => {
    if (agent) {
      setModal(undefined);
      if (property) trackCall(property, agent?.accountNumber);
      if (agent.officePhone?.displayNumber) window.open(`tel:${agent.officePhone.displayNumber}`);
    } else if (contact) {
      setModal(undefined);
      if (property) trackCall(property, contact.accountNumber);
      window.open(`tel:${contact.phoneNumber.displayNumber}`);
    } else if (property) {
      const { contacts } = property;

      if (contacts) {
        if (contacts?.all.length > 1) {
          // get all agents that have phone enabled
          const items = contacts.all
            .filter((a) => a.phone)
            .map((a) => ({
              label: a.organisation,
              id: a.phoneNumber.displayNumber,
              logo: a.logos.standard,
            }));

          const onSelected = ({ id, label }: AgentNumber) => {
            // find account number
            const filteredAccount = contacts.all.filter((a) => a.organisation === label);
            trackCall(property, filteredAccount[0].accountNumber);
            // open telephone link
            window.open(`tel:${id}`);
          };

          setModal(<CallAgentModal show onClose={() => setModal(undefined)} items={items} onSelected={onSelected} />);
        } else {
          setModal(undefined);
          const a = contacts.default || contacts.all[0];
          trackCall(property, a.accountNumber);
          window.open(`tel:${a.phoneNumber.displayNumber}`);
        }
      }
    }
  };

  const onRevealText = (property?: Property, contact?: Contact, agent?: Agent, page?: AgentEventPage) => {
    if (agent) {
      if (page) trackAgentCall(agent, page);
      const item = {
        label: `${agent.organisation}${agent.organisationBranch ? ` (${agent.organisationBranch})` : ''}`,
        id: agent.officePhone?.displayNumber || '',
        logo: agent.logo?.standard,
      };
      setModal(
        <CallAgentModal
          show
          onClose={() => setModal(undefined)}
          items={[item]}
          onSelected={() => {}}
          developer={agent.developer}
        />,
      );
    } else if (contact) {
      if (property) trackCall(property, contact.accountNumber);
      const item = {
        label: contact.organisation,
        id: contact.phoneNumber.displayNumber,
        logo: contact.logos.standard,
      };
      setModal(
        <CallAgentModal
          show
          onClose={() => setModal(undefined)}
          items={[item]}
          onSelected={() => {}}
          developer={contact.developer}
        />,
      );
    } else if (property) {
      const { contacts } = property;

      if (contacts) {
        if (contacts?.all.length > 1) {
          // get all agents that have phone enabled
          const items = contacts.all
            .filter((a) => a.phone)
            .map((a) => {
              trackCall(property, a.accountNumber);
              return {
                label: a.organisation,
                id: a.phoneNumber.displayNumber,
                logo: a.logos.standard,
              };
            });

          setModal(<CallAgentModal show onClose={() => setModal(undefined)} items={items} onSelected={() => {}} />);
        } else {
          setModal(undefined);
          const a = contacts.default || contacts.all[0];
          setRevealText(a.phoneNumber.displayNumber);
          trackCall(property, a.accountNumber);
        }
      }
    }
  };

  return {
    callAgent,
    modal,
    getPhoneNumber,
    phoneNumber,
    trackCall,
    onRevealText,
    revealText,
    revealed: revealText !== REVEAL_TEXT,
  };
};

export default useCallAgent;
